import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import HeroSection from '../components/HeroSection';
import WorldMap from '../components/WorldMap';
import ClientLogos from '../components/ClientLogos';
import AboutSection from '../components/AboutSection';
import TeamSection from '../components/TeamSection';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';
import MaintenanceMode from '../components/MaintenanceMode';
import { getConsent } from '../components/CookieBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [content, setContent] = useState({});
  const [locations, setLocations] = useState([]);
  const [clientLogos, setClientLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ maintenance_mode: false, show_hero_text: false });

  useEffect(() => {
    fetchData();
  }, []);

  // Besuch erst nach ausdrücklicher Zustimmung zählen.
  // Wird auch nachgeholt, wenn im Cookie-Hinweis zugestimmt wird.
  useEffect(() => {
    let gezaehlt = false;

    const zaehlen = () => {
      if (gezaehlt || getConsent() !== 'accepted') return;
      gezaehlt = true;
      fetch(`${API_URL}/api/track-visit`, { method: 'POST' }).catch(() => {});
    };

    zaehlen();
    const aufEntscheidung = (e) => {
      if (e.detail === 'accepted') zaehlen();
    };
    window.addEventListener('sd-consent', aufEntscheidung);
    return () => window.removeEventListener('sd-consent', aufEntscheidung);
  }, []);

  const fetchData = async () => {
    try {
      // Kein Seed-Aufruf mehr: Das Befüllen der Datenbank gehört nicht in
      // den Seitenaufruf. Parallele Aufrufe hatten Standorte doppelt angelegt.
      // Alle Inhalte liegen bereits in der Datenbank.
      const [contentRes, locationsRes, settingsRes, logosRes] = await Promise.all([
        fetch(`${API_URL}/api/content`),
        fetch(`${API_URL}/api/locations`),
        fetch(`${API_URL}/api/settings`).catch(() => ({ ok: false })),
        fetch(`${API_URL}/api/client-logos`).catch(() => ({ ok: false })),
      ]);

      const [contentData, locationsData] = await Promise.all([
        contentRes.json(),
        locationsRes.json(),
      ]);

      setLocations(locationsData);

      // Check settings
      let settingsData = { maintenance_mode: false, show_hero_text: false };
      if (settingsRes.ok) {
        settingsData = await settingsRes.json();
      }
      setSettings(settingsData);

      // Client logos
      let logosData = [];
      if (logosRes.ok) {
        logosData = await logosRes.json();
      }
      setClientLogos(logosData);

      // Convert content array to object
      const contentObj = {};
      contentData.forEach(item => {
        contentObj[item.key] = item.value;
      });

      setContent(contentObj);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm uppercase tracking-wider">Loading</p>
        </div>
      </div>
    );
  }

  // Show maintenance mode if enabled
  if (settings.maintenance_mode) {
    return <MaintenanceMode settings={settings} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Seitenüberschrift. Ist der Hero-Text im Admin aktiv, bringt der Hero
          selbst eine sichtbare h1 mit; sonst steht sie hier unsichtbar für
          Suchmaschinen und Screenreader. */}
      {!settings.show_hero_text && (
        <h1 className="sr-only">
          {(content.hero_headline || 'Wir geben Marken eine Richtung').replace(/\n/g, ' ')}
          {' \u2014 Someones Direction, Creative Production aus Wien'}
        </h1>
      )}

      <HeroSection content={content} settings={settings} />
      <WorldMap locations={locations} />
      <ClientLogos logos={clientLogos} />
      <AboutSection content={content} />
      <TeamSection content={content} />
      <CTASection content={content} settings={settings} />
      <Footer settings={settings} />
    </div>
  );
}
